// ─────────────────────────────────────────────────────────────────────────────
// RFISection — Requests for Information register.
// Construction Engineering Cockpit aesthetic. Token-driven, primitive-driven.
// Logic preserved from original; presentation rebuilt on top of ./ui.jsx.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo, useState } from "react";
import { tokens } from "./theme";
import {
  Card, BlueprintGrid, Reveal, Eyebrow,
  Input, Select, Textarea, FormRow, FormGroupDivider,
  Button, Badge,
  Stat,
  Table, Th, Td,
  Empty,
} from "./ui";
import { nextRfiNumber } from "../utils/rfiNumbering";

const t = tokens;

// ─── Status → badge variant mapping ──────────────────────────────────────────
function statusVariant(status) {
  switch (status) {
    case "Open":         return "danger";
    case "Under Review": return "warning";
    case "Responded":    return "info";
    case "Closed":       return "success";
    default:             return "default";
  }
}

// ─── Page-specific icons ─────────────────────────────────────────────────────
const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  save:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  view:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  link:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function RFISection({
  rfiForm,
  setRfiForm,
  handleAddRfi,
  rfis,
  currentProjectId,
  handleEditRfi,
  handleDeleteRfi,
  editingRfiId,
  clearRfiForm,
  variations,
  handleCreateVariationFromRfi,
  handleViewItem,
}) {
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("number_asc");

  const updateForm = (field, value) =>
    setRfiForm({ ...rfiForm, [field]: value, projectId: currentProjectId });

  // Next auto-assigned RFI number for this project (001, 002, …). Previews the
  // number a new RFI will get; handleAddRfi assigns the same value on save.
  const nextNo = useMemo(
    () => nextRfiNumber(rfis.filter((item) => item.projectId === currentProjectId)),
    [rfis, currentProjectId]
  );

  // ── Filter + sort (logic preserved from original) ─────────────────────────
  const rows = useMemo(() => {
    const filtered = rfis.filter((item) => item.projectId === currentProjectId);
    const statusFiltered = statusFilter === "All"
      ? filtered
      : filtered.filter((item) => (item.status || "") === statusFilter);

    return [...statusFiltered].sort((a, b) => {
      if (sortBy === "number_desc") {
        return (b.rfiNo || "").localeCompare(a.rfiNo || "", undefined, { numeric: true, sensitivity: "base" });
      }
      if (sortBy === "status_asc") {
        return (a.status || "").localeCompare(b.status || "", undefined, { numeric: true, sensitivity: "base" });
      }
      if (sortBy === "status_desc") {
        return (b.status || "").localeCompare(a.status || "", undefined, { numeric: true, sensitivity: "base" });
      }
      return (a.rfiNo || "").localeCompare(b.rfiNo || "", undefined, { numeric: true, sensitivity: "base" });
    });
  }, [rfis, currentProjectId, statusFilter, sortBy]);

  const projectVariations = (variations || []).filter(
    (item) => item.projectId === currentProjectId
  );

  // ── KPI counts for the header strip ───────────────────────────────────────
  const counts = useMemo(() => {
    const all = rfis.filter((r) => r.projectId === currentProjectId);
    return {
      total:    all.length,
      open:     all.filter((r) => r.status === "Open").length,
      review:   all.filter((r) => r.status === "Under Review").length,
      closed:   all.filter((r) => r.status === "Closed").length,
      voTrigger: all.filter((r) => r.responseCreatesVariation === "Yes" && !r.relatedVariationId).length,
    };
  }, [rfis, currentProjectId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{ position: "relative", display: "grid", gap: 18, fontFamily: t.font.body }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0}>
          <Card padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
            }} />
            <div style={{ padding: "24px 26px", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Eyebrow>Information Control</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                  <span style={{
                    fontFamily: t.font.mono, fontSize: 11, fontWeight: 600,
                    color: t.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                </div>
                <h1 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: t.color.ink,
                }}>RFI Register</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5, maxWidth: 640 }}>
                  Capture every technical query, track responses and flag any that may
                  trigger a variation order.
                </p>
              </div>
              {editingRfiId && (
                <Button variant="subtle" onClick={clearRfiForm} icon={I.cancel}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </Card>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 10,
          }}>
            <Stat label="Total RFIs"     rawValue={counts.total}     format={(v) => Math.round(v)} value={counts.total}     accent={t.color.primary} delay={80}  helper="Across this project" />
            <Stat label="Open"           rawValue={counts.open}      format={(v) => Math.round(v)} value={counts.open}      accent={t.color.danger}  delay={140} helper="Awaiting response" />
            <Stat label="Under Review"   rawValue={counts.review}    format={(v) => Math.round(v)} value={counts.review}    accent={t.color.warning} delay={200} helper="Being assessed" />
            <Stat label="Closed"         rawValue={counts.closed}    format={(v) => Math.round(v)} value={counts.closed}    accent={t.color.success} delay={260} helper="Responded & accepted" />
            <Stat label="VO Triggers"    rawValue={counts.voTrigger} format={(v) => Math.round(v)} value={counts.voTrigger} accent={t.color.accent}  delay={320} helper="Need a variation" />
          </div>
        </Reveal>

        {/* ───── FORM CARD ───── */}
        <Reveal delay={220}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow>{editingRfiId ? "Editing record" : "New record"}</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>RFI Details</h3>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              <FormGroupDivider>Identity</FormGroupDivider>

              <FormRow label="RFI Number" helper={editingRfiId ? "Auto-assigned reference (locked)" : "Auto-assigned — next in sequence for this project"}>
                <Input
                  mono
                  readOnly
                  value={editingRfiId ? (rfiForm.rfiNo || "") : nextNo}
                  style={{ background: t.color.surfaceMuted || "#F3F4F6", cursor: "default" }}
                />
              </FormRow>
              <FormRow label="Date Submitted" helper="Date the RFI was issued or submitted">
                <Input type="date" value={rfiForm.dateSubmitted || ""} onChange={(e) => updateForm("dateSubmitted", e.target.value)} />
              </FormRow>
              <FormRow label="Originator" helper="Person or organisation raising the RFI">
                <Input value={rfiForm.originator || ""} onChange={(e) => updateForm("originator", e.target.value)} />
              </FormRow>
              <FormRow label="Contractor / Subcontractor" helper="The contractor or subcontractor linked to the query">
                <Input value={rfiForm.contractor || ""} onChange={(e) => updateForm("contractor", e.target.value)} />
              </FormRow>
              <FormRow label="Discipline" helper="Civil, mechanical, electrical, process, structural, etc.">
                <Input value={rfiForm.discipline || ""} onChange={(e) => updateForm("discipline", e.target.value)} />
              </FormRow>

              <FormGroupDivider>The Query</FormGroupDivider>

              <FormRow label="Subject / Description" helper="Short description of the issue needing clarification">
                <Input value={rfiForm.subject || ""} onChange={(e) => updateForm("subject", e.target.value)} />
              </FormRow>
              <FormRow label="Reason for RFI" helper="Why the RFI is necessary">
                <Textarea rows={3} value={rfiForm.reason || ""} onChange={(e) => updateForm("reason", e.target.value)} />
              </FormRow>
              <FormRow label="Drawing / Spec Reference" helper="Relevant drawing, specification, BOQ or clause reference">
                <Input mono value={rfiForm.reference || ""} onChange={(e) => updateForm("reference", e.target.value)} />
              </FormRow>
              <FormRow label="Required Response Date" helper="Date by which the team needs a response">
                <Input type="date" value={rfiForm.requiredResponseDate || ""} onChange={(e) => updateForm("requiredResponseDate", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Response</FormGroupDivider>

              <FormRow label="Date Responded" helper="Date the formal response was issued">
                <Input type="date" value={rfiForm.dateResponded || ""} onChange={(e) => updateForm("dateResponded", e.target.value)} />
              </FormRow>
              <FormRow label="Response Summary" helper="Summarise the response issued to the RFI">
                <Textarea rows={3} value={rfiForm.responseSummary || ""} onChange={(e) => updateForm("responseSummary", e.target.value)} />
              </FormRow>
              <FormRow label="Status" helper="Current RFI status">
                <Select value={rfiForm.status || "Open"} onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="Open">Open</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Responded">Responded</option>
                  <option value="Closed">Closed</option>
                </Select>
              </FormRow>
              <FormRow label="Impact" helper="State any impact on cost, time, scope or quality">
                <Input value={rfiForm.impact || ""} onChange={(e) => updateForm("impact", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Commercial Linkage</FormGroupDivider>

              <FormRow label="Linked Variation Order" helper="If already linked, select the related variation order">
                <Select value={rfiForm.relatedVariationId || ""} onChange={(e) => updateForm("relatedVariationId", e.target.value)}>
                  <option value="">Not linked</option>
                  {projectVariations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.variationNo} — {item.voDescription || item.title}
                    </option>
                  ))}
                </Select>
              </FormRow>
              <FormRow label="Response Creates New Work" helper="Select yes if the RFI response instructs additional or changed work requiring a VO">
                <Select value={rfiForm.responseCreatesVariation || "No"} onChange={(e) => updateForm("responseCreatesVariation", e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </Select>
              </FormRow>
              <FormRow label="Notes" helper="Any extra notes for internal tracking">
                <Textarea rows={3} value={rfiForm.notes || ""} onChange={(e) => updateForm("notes", e.target.value)} />
              </FormRow>

              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                <Button variant="accent" onClick={handleAddRfi} icon={editingRfiId ? I.save : I.add}>
                  {editingRfiId ? "Update RFI" : "Save RFI"}
                </Button>
                {editingRfiId && (
                  <Button variant="subtle" onClick={clearRfiForm} icon={I.cancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ───── TABLE CARD ───── */}
        <Reveal delay={300}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 16px",
              display: "flex", alignItems: "flex-end", justifyContent: "space-between",
              gap: 16, flexWrap: "wrap",
            }}>
              <div>
                <Eyebrow>Register</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0", fontFamily: t.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
                }}>All RFIs</h3>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ display: "grid", gap: 4, minWidth: 180 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: t.color.inkSubtle,
                  }}>Status</span>
                  <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="All">All statuses</option>
                    <option value="Open">Open</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Responded">Responded</option>
                    <option value="Closed">Closed</option>
                  </Select>
                </div>
                <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: t.color.inkSubtle,
                  }}>Sort</span>
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="number_asc">RFI Number (A–Z)</option>
                    <option value="number_desc">RFI Number (Z–A)</option>
                    <option value="status_asc">Status (A–Z)</option>
                    <option value="status_desc">Status (Z–A)</option>
                  </Select>
                </div>
              </div>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              {rows.length === 0 ? (
                <Empty
                  title="No RFIs found"
                  message="Adjust the status filter, or capture a new RFI using the form above."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>RFI No</Th>
                      <Th>Date Submitted</Th>
                      <Th>Subject</Th>
                      <Th>Status</Th>
                      <Th align="center">Creates VO</Th>
                      <Th>Linked VO</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const linkedVariation = projectVariations.find((v) => v.id === row.relatedVariationId);
                      const showCreateVo = row.responseCreatesVariation === "Yes" && !row.relatedVariationId;
                      return (
                        <tr key={row.id}>
                          <Td mono weight={700}>{row.rfiNo || "—"}</Td>
                          <Td mono>{row.dateSubmitted || "—"}</Td>
                          <Td>{row.subject || "—"}</Td>
                          <Td><Badge variant={statusVariant(row.status)}>{row.status || "—"}</Badge></Td>
                          <Td align="center">
                            {row.responseCreatesVariation === "Yes"
                              ? <Badge variant="accent" size="sm">Yes</Badge>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td mono>
                            {linkedVariation
                              ? <span style={{ color: t.color.primary, fontWeight: 700 }}>
                                  {linkedVariation.variationNo}
                                </span>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td align="right">
                            <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <Button size="sm" variant="success" onClick={() => handleViewItem(`RFI ${row.rfiNo}`, row)} icon={I.view}>
                                View
                              </Button>
                              <Button size="sm" variant="subtle" onClick={() => handleEditRfi(row)} icon={I.edit}>
                                Edit
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => handleDeleteRfi(row.id)} icon={I.trash}>
                                Delete
                              </Button>
                              {showCreateVo && (
                                <Button size="sm" variant="accent" onClick={() => handleCreateVariationFromRfi(row)} icon={I.link}>
                                  Create VO
                                </Button>
                              )}
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </Reveal>

      </div>
    </section>
  );
}

export default memo(RFISection);
